/**
 * Loading messages and rotator selector for the Marketing Roast Machine.
 *
 * Owns the curated list of cheeky messages shown on the Loading_Screen and
 * a pure selector that maps elapsed milliseconds to the message currently
 * on-screen. The `LoadingScreen` component drives the 3-second rotation
 * cadence (Requirement 3.3) and pulls from this content list so output
 * stays consistent and on-brand (Requirement 3.4).
 *
 * This module is pure: no DOM imports, no timers, no side effects beyond
 * the module-init assertion that the curated list is at least 6 entries
 * long (Property 4). That check runs exactly once at import time.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Loading Messages"
 * and Requirements 3.3, 3.4.
 */

/**
 * Curated cheeky messages shown while the Roast_Generator is producing a
 * Roast_Result. The rotator cycles modulo the list length, so runs longer
 * than 8×3s = 24s continue to feel fresh.
 *
 * The list is `Object.freeze`d so consumers cannot mutate shared content
 * at runtime; the TypeScript type is `readonly string[]` to match the
 * design-specified module shape.
 */
export const LOADING_MESSAGES: readonly string[] = Object.freeze([
  'Consulting the marketing gods…',
  'Cross-referencing your brief with 1,000 bad ads…',
  'Polishing your brand so hard it squeaks…',
  'Loading industrial-grade sass…',
  'Asking a focus group of one intern…',
  'Debugging your taglines…',
  'Translating corporate into human…',
  'Rounding up stray buzzwords…',
]);

// Module-init invariant: Requirement 3.3 mandates at least 6 rotating
// messages. Failing loudly here prevents a silently under-seeded list from
// shipping to a booth.
if (LOADING_MESSAGES.length < 6) {
  throw new Error(
    `LOADING_MESSAGES must contain at least 6 entries, found ${LOADING_MESSAGES.length}`,
  );
}

/**
 * Select the message to display at a given elapsed time on the
 * Loading_Screen.
 *
 * Advances to the next message every 3000 ms and wraps around modulo the
 * list length, so the formula is:
 *
 *     messages[floor(elapsedMs / 3000) mod messages.length]
 *
 * Defensive handling:
 * - Non-finite or negative `elapsedMs` values are clamped to 0 so a
 *   misbehaving clock cannot crash the booth.
 * - An empty `messages` array throws `RangeError`, mirroring the
 *   module-init check so callers who swap in a custom list get the same
 *   loud failure mode.
 *
 * @param elapsedMs milliseconds since the Loading_Screen was displayed
 * @param messages  message list to rotate over (defaults to `LOADING_MESSAGES`)
 * @returns         the message to display at `elapsedMs`
 */
export function selectMessage(
  elapsedMs: number,
  messages: readonly string[] = LOADING_MESSAGES,
): string {
  if (messages.length === 0) {
    throw new RangeError('selectMessage(messages): list must not be empty');
  }

  const safeElapsed =
    Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  const index = Math.floor(safeElapsed / 3000) % messages.length;
  // `messages.length > 0` and `index` is a non-negative integer < length,
  // so this indexed access is always defined.
  return messages[index] as string;
}
